<template>
  <el-dialog
    v-model="visible"
    :title="step === 'viapi' ? '人照比对' : '告知函实人认证'"
    width="420px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="step === 'ready'"
    destroy-on-close
    @closed="onClosed"
  >
    <!-- VIAPI：与登录实人一致的圆形镜头 -->
    <div v-if="step === 'viapi'" class="viapi-box">
      <p class="form-title">请正对镜头完成自拍核验</p>
      <div v-show="!preview" class="cam-wrap">
        <div ref="camHost" class="cam-host" />
        <p class="tip">{{ tip }}</p>
        <el-button type="primary" class="btn" :disabled="!camReady || busy" @click="capture">
          {{ busy ? '处理中...' : '拍照核验' }}
        </el-button>
      </div>
      <div v-show="!!preview" class="preview-wrap">
        <img :src="preview" class="preview-img" alt="preview" />
        <div class="btn-row">
          <el-button @click="retake">重拍</el-button>
          <el-button type="primary" :loading="busy" @click="submitViapi">确认提交</el-button>
        </div>
      </div>
    </div>

    <template v-else>
      <el-form label-position="top" @submit.prevent>
        <el-form-item label="姓名" required>
          <el-input :model-value="displayName" readonly />
        </el-form-item>
        <el-form-item label="身份证号" required>
          <el-input :model-value="displayIdCard" readonly />
        </el-form-item>
      </el-form>
      <p class="tip">
        {{
          verifyMode === 'viapi_liveness'
            ? '已有人脸底图，将通过自拍人照比对完成核验'
            : '将跳转炫光活体页面完成刷脸建档'
        }}
      </p>
    </template>

    <template v-if="step === 'ready'" #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="onStart">开始认证</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import {
  initAgreementLiveness,
  queryAgreementLiveness,
  viapiVerifyAgreementLiveness,
} from '@/api/agreement'

const props = defineProps<{
  modelValue: boolean
  templateId?: string | number
  ticketId?: string
  realName?: string
  idCard?: string
  verifyMode?: string
  returnUrl?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'passed', payload: { certifyId: string; faceCaptureUrl?: string }): void
}>()

const visible = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

const submitting = ref(false)
const step = ref<'ready' | 'viapi'>('ready')
const verifyMode = ref(props.verifyMode || 'glow')
const certifyId = ref('')
const tip = ref('正在打开前置摄像头...')
const camReady = ref(false)
const busy = ref(false)
const preview = ref('')
const camHost = ref<HTMLElement | null>(null)
let stream: MediaStream | null = null
let videoEl: HTMLVideoElement | null = null

const displayName = computed(() => (props.realName || '').trim())
const displayIdCard = computed(() => (props.idCard || '').trim().toUpperCase())

watch(
  () => props.modelValue,
  (v) => {
    if (v) {
      step.value = 'ready'
      verifyMode.value = props.verifyMode || 'glow'
      certifyId.value = ''
      preview.value = ''
    }
  },
)

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

const waitVideoFirstFrame = async (video: HTMLVideoElement, timeoutMs = 8000) => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (video.videoWidth > 0 && video.videoHeight > 0) return true
    await wait(50)
  }
  return video.videoWidth > 0
}

const acquireFrontStream = async () => {
  const tryConstraints: MediaStreamConstraints[] = [
    {
      video: {
        facingMode: { ideal: 'user' },
        width: { ideal: 640 },
        height: { ideal: 480 },
      },
      audio: false,
    },
    { video: { facingMode: 'user' }, audio: false },
    { video: true, audio: false },
  ]
  let lastErr: unknown = null
  for (const c of tryConstraints) {
    try {
      return await navigator.mediaDevices.getUserMedia(c)
    } catch (e) {
      lastErr = e
    }
  }
  throw lastErr || new Error('无法打开摄像头')
}

const stopCam = () => {
  try {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop())
      stream = null
    }
    if (videoEl) {
      try {
        videoEl.srcObject = null
      } catch (_) {}
      videoEl = null
    }
    const host = camHost.value
    if (host) host.innerHTML = ''
  } catch (e) {}
  camReady.value = false
}

const openCam = async () => {
  tip.value = '正在打开前置摄像头...'
  camReady.value = false
  stopCam()
  await nextTick()
  await wait(50)
  if (!navigator.mediaDevices?.getUserMedia) {
    tip.value = '当前环境不支持摄像头'
    return
  }
  try {
    let host = camHost.value
    if (!host) {
      await wait(50)
      await nextTick()
      host = camHost.value
    }
    if (!host) {
      tip.value = '摄像头预览节点未就绪，请重试'
      return
    }
    const video = document.createElement('video')
    video.setAttribute('playsinline', 'true')
    video.setAttribute('webkit-playsinline', 'true')
    video.muted = true
    video.autoplay = true
    video.playsInline = true
    video.style.cssText =
      'display:block;width:100%;height:100%;object-fit:cover;transform:scaleX(-1);background:#111;'
    host.innerHTML = ''
    host.appendChild(video)
    videoEl = video
    stream = await acquireFrontStream()
    video.srcObject = stream
    try {
      await video.play()
    } catch (_) {}
    const ok = await waitVideoFirstFrame(video)
    if (!ok) throw new Error('摄像头预览无画面')
    camReady.value = true
    tip.value = '请正对镜头后拍照'
  } catch (e) {
    stopCam()
    tip.value = '无法打开摄像头，请检查权限后重试'
  }
}

const onStart = async () => {
  if (!displayName.value || !displayIdCard.value) {
    ElMessage.warning('学籍姓名或身份证缺失，请联系管理员完善后再认证')
    return
  }
  if (!props.templateId) {
    ElMessage.warning('协议模板缺失')
    return
  }

  submitting.value = true
  try {
    const returnUrl =
      props.returnUrl ||
      `${window.location.origin}${window.location.pathname}${window.location.search}`
    const initRes: any = await initAgreementLiveness({
      templateId: props.templateId,
      realName: displayName.value,
      idCard: displayIdCard.value,
      returnUrl,
      ticketId: props.ticketId || undefined,
    })
    certifyId.value = initRes?.certifyId || ''
    const mode = initRes?.verifyMode || verifyMode.value || 'glow'
    verifyMode.value = mode
    if (!certifyId.value) {
      ElMessage.error('发起认证失败')
      return
    }
    sessionStorage.setItem(
      'agreement_liveness_pending',
      JSON.stringify({
        certifyId: certifyId.value,
        templateId: props.templateId,
        ticketId: props.ticketId || '',
      }),
    )
    if (mode === 'viapi_liveness' || !initRes?.certifyUrl) {
      step.value = 'viapi'
      await nextTick()
      openCam()
      return
    }
    window.location.href = initRes.certifyUrl
  } catch (e: any) {
    ElMessage.error(e?.errorMessage || e?.message || '发起认证失败')
  } finally {
    submitting.value = false
  }
}

const capture = () => {
  if (!camReady.value || busy.value || !videoEl?.videoWidth) return
  const canvas = document.createElement('canvas')
  canvas.width = videoEl.videoWidth
  canvas.height = videoEl.videoHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.translate(canvas.width, 0)
  ctx.scale(-1, 1)
  ctx.drawImage(videoEl, 0, 0)
  preview.value = canvas.toDataURL('image/jpeg', 0.92)
  stopCam()
}

const retake = async () => {
  preview.value = ''
  tip.value = '正在重新打开摄像头...'
  await nextTick()
  await openCam()
}

const submitViapi = async () => {
  if (!preview.value || !certifyId.value || busy.value) return
  busy.value = true
  try {
    const res: any = await viapiVerifyAgreementLiveness({
      certifyId: certifyId.value,
      imageBase64: preview.value,
      ticketId: props.ticketId || undefined,
    })
    if (res?.passed) {
      sessionStorage.removeItem('agreement_liveness_pending')
      emit('passed', { certifyId: certifyId.value, faceCaptureUrl: res.faceCaptureUrl })
      visible.value = false
      ElMessage.success('认证成功')
    } else {
      ElMessage.error(res?.message || '人照比对未通过')
      preview.value = ''
      await nextTick()
      await openCam()
    }
  } catch (e: any) {
    ElMessage.error(e?.errorMessage || e?.message || '核验失败')
  } finally {
    busy.value = false
  }
}

const onClosed = () => {
  stopCam()
  step.value = 'ready'
  preview.value = ''
}

onBeforeUnmount(() => stopCam())

defineExpose({
  queryPending: async () => {
    const raw = sessionStorage.getItem('agreement_liveness_pending')
    if (!raw) return null
    try {
      const pending = JSON.parse(raw)
      const res: any = await queryAgreementLiveness(pending.certifyId)
      if (res?.passed) {
        sessionStorage.removeItem('agreement_liveness_pending')
        emit('passed', { certifyId: pending.certifyId, faceCaptureUrl: res.faceCaptureUrl })
      }
      return res
    } catch (e) {
      console.error(e)
      return null
    }
  },
})
</script>

<style scoped lang="scss">
.form-title {
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px;
  color: #0f172a;
}
.tip {
  margin: 8px 0 12px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  text-align: center;
}
.btn {
  width: 100%;
  margin-top: 8px;
}
.cam-host {
  width: 260px;
  height: 260px;
  margin: 0 auto;
  border-radius: 50%;
  overflow: hidden;
  background: #111;
}
.preview-img {
  width: 260px;
  height: 260px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  margin: 0 auto;
}
.btn-row {
  display: flex;
  gap: 12px;
  margin-top: 16px;
}
.btn-row .el-button {
  flex: 1;
}
</style>
